import { Modal, Alert, Input, Button, Table, Icon, theme, IconButton } from '@salecycle/ui-component-library';
import styled from 'styled-components';
import { useState, useMemo, useEffect } from 'react';
import { isValidIpAddress } from 'core/ui/authenticator/validIpAddressChecks';
import { Column } from 'react-table';
import { useTranslation } from 'react-i18next';
import { useCampaignSettings } from '../../CampaignWorkflowTab/state/CampaignSettings/hooks/useCampaignSettings';

const Container = styled.div`
  display: block;
`;

const SubHeader = styled.div`
  margin-bottom: 32px;
`;

const StyledNewIPContainer = styled.div`
  display: grid;
  grid-gap: 32px;
  grid-template-columns: auto auto auto;
  align-items: flex-end;
  margin-bottom: 32px;
`;
const AddNewIPContainer = styled.div`
  margin: 32px 0;
`;
const IconContainerContainer = styled.div`
  display: flex;
  align-items: center;
`;
const CloseIcon = styled.div`
  margin-left: 16px;
  cursor: pointer;
`;

interface Props {
  open: boolean;
  ipName?: string;
  ipAddress?: string;
  onCloseButtonClicked: () => void;
  onOkButtonClicked: () => void;
}
const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;

  & button {
    background: none;
    border: none;
  }
`;

export function IPWhitelistModal(props: Props) {
  const { allowList, setAllowList } = useCampaignSettings();

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [nameError, setNameError] = useState(false);
  const [ipError, setIpError] = useState(false);
  const [newIP, setNewIP] = useState(false);
  const [editRowIndex, setEditRowIndex] = useState();
  const [ips, setIPS] = useState([]);
  const [deleteMode, setDeleteMode] = useState(false);
  const [enableEditeButton, setDisableEditeButton] = useState(true);
  const validIp = useMemo(() => isValidIpAddress(address), [address]);

  const { t } = useTranslation('campaigns');

  const tableColumns: Column[] = [
    {
      Header: 'Name',
      accessor: 'name',
    },
    {
      Header: 'IP Address',
      accessor: 'ipAddress',
    },
    {
      accessor: 'type',
      // eslint-disable-next-line react/display-name
      Cell: row => (
        <ButtonWrapper>
          <button onClick={() => onEditClick(row)}>
            <Icon icon="cog" color={theme.vars.newColor.darkGS2} />
          </button>
          <button onClick={() => onDeletClick(row)}>
            <Icon icon="trash" color={theme.vars.newColor.darkGS2} />
          </button>
        </ButtonWrapper>
      ),
      width: 125,
    },
  ];

  useEffect(() => {
    setIPS(allowList ? allowList : []);
  }, [allowList, props.onCloseButtonClicked]);

  const onAddIP = () => {
    const validName = name.length !== 0;

    !validName ? setNameError(true) : setNameError(false);

    !validIp ? setIpError(true) : setIpError(false);

    if (validName && validIp) {
      if (editRowIndex !== undefined) {
        const data = [...ips];
        data.splice(editRowIndex, 1, { name: name, ipAddress: address });
        setIPS(data);
        setEditRowIndex(undefined);
      } else {
        setIPS([...ips, { name: name, ipAddress: address }]);
      }
      resetData();
    }
  };

  function resetData() {
    setNewIP(true);
    setName('');
    setAddress('');
    setNameError(false);
    setIpError(false);
  }

  function onSaveClick() {
    setAllowList(ips);
  }

  function onDeletClick(row) {
    const data = [...ips];
    data.splice(row.index, 1);
    if (data.length === 0) setDeleteMode(true);
    setIPS(data);
  }

  function onEditClick(row) {
    setDisableEditeButton(true);
    const data = [...ips];
    const ipAddress = data[row.index].ipAddress;
    const name = data[row.index].name;
    setName(name);
    setAddress(ipAddress);
    setEditRowIndex(row.index);
  }

  return (
    <>
      <Modal
        minWidth={900}
        heading={t('onsite.campaignSettingsTab.allowList.modal.heading')}
        isOpen={props.open}
        variation="clean"
        onCloseButtonClicked={() => {
          setDeleteMode(false);
          setEditRowIndex(undefined);
          resetData();
          props.onCloseButtonClicked();
        }}
        cancelButtonAriaLabel={t('onsite.campaignSettingsTab.allowList.modal.cancelButtonAriaLabel')}
        onOkButtonClicked={() => {
          onSaveClick();
          setDeleteMode(false);
          props.onOkButtonClicked();
        }}
        okButtonLabel={t('onsite.save')}
        okButtonAriaLabel={'Save IP AllowList records'}
        okButtonDisabled={!(ips?.length > 0 || deleteMode)}
      >
        <Container>
          <SubHeader>Allow people with the below IP addresses to see your website content when testing.</SubHeader>
          {!newIP || editRowIndex !== undefined || !ips.length ? (
            <StyledNewIPContainer>
              <Input
                ariaLabel="IP name"
                placeholder="e.g clients office"
                label="IP name"
                hint="IP name allows you to keep IP addresses organised. You can choose anything as an IP name but we recommended keeping it short and relevant."
                spacingBelow={false}
                onChange={value => {
                  setName(value);
                  editRowIndex !== undefined && setDisableEditeButton(false);
                }}
                value={name}
                validationMessage={nameError && 'Name is required'}
                validationState={name && 'complete'}
              />
              <Input
                ariaLabel="IP address"
                placeholder="e.g 123.123.123.123"
                label="IP address (. and numbers only)"
                hint="IP addresses are unique strings of characters used to identify computers over the internet. You can find this by typing ‘What’s my IP?’ into Google or visiting www.whatismyip.com"
                spacingBelow={false}
                onChange={value => {
                  setAddress(value);
                  editRowIndex !== undefined && setDisableEditeButton(false);
                }}
                value={address}
                validationMessage={ipError && 'IP address should be of the form 123.123.123.123'}
                validationState={validIp && 'complete'}
              />
              <IconContainerContainer>
                <Button
                  ariaLabel={editRowIndex !== undefined ? 'Edit IP' : 'Add IP'}
                  onClick={onAddIP}
                  variation="primary"
                  disabled={editRowIndex !== undefined ? enableEditeButton : name === '' || address === ''}
                >
                  {editRowIndex !== undefined ? 'Edit IP' : 'Add IP'}
                </Button>

                {ips?.length > 0 && editRowIndex === undefined && (
                  <CloseIcon>
                    <IconButton ariaLabel="Clear Button" icon="times" onClick={() => setNewIP(true)} />
                  </CloseIcon>
                )}
              </IconContainerContainer>
            </StyledNewIPContainer>
          ) : (
            <AddNewIPContainer>
              <Button
                onClick={() => setNewIP(false)}
                variation="secondary"
                ariaLabel="Add a new IP"
                title="Add a new IP"
              />
            </AddNewIPContainer>
          )}
          {ips?.length > 0 && <Table data={ips} columns={tableColumns} />}
          {ips?.length === 0 && (
            <div
              style={{
                margin: '32px 0 10px 0',
              }}
            >
              <Alert variation="info" heading="You don’t have any IP addresses on allowlist for this campaign yet" />
            </div>
          )}
        </Container>
      </Modal>
    </>
  );
}
